/**
 * The launch screen's memory of the rave (the user, 2026-10-07).
 *
 * The launch screen, all in `index.html` so it paints before the bundle has
 * even arrived. Inside a rave it opens ON the rave — its logo, dates and place
 * from the first frame when this origin has opened it before (the user,
 * 2026-10-07: "entering OZORA, show OZORA directly"), or as soon as the early
 * `/tenant` answer names it on a first visit. Elsewhere, the MyRave lockup.
 * Once nothing is loading any more, it opens onto the app. The MyRave mark
 * turns from the first frame to the last.
 *
 * The rave's logo is not in `/tenant` — it arrives with the bundle — and the
 * launch screen cannot wait for either: it runs before any script of ours and
 * must work with no signal. So the rave app writes what the launch screen
 * needs here every time it opens, and the NEXT launch reads it. Storage is per
 * origin, so `ozora.<platform>` only ever shows OZORA, and `app.`/`login.`
 * never have one at all.
 *
 * The inline script in `index.html` reads this key by its literal name
 * (`splash.test.ts` holds the two together).
 */
import { inPreview } from '@/lib/preview';
import { formatDateRange, type EditionBundle } from '@myrave/shared';
import { readJson, writeJson } from '@/lib/storage';
import { localDateRange } from '@/ui';

export const SPLASH_NAME = 'splash';

export interface SplashRave {
  /** The edition's name, shown when there is no logo. */
  name: string;
  /** The festival's own name — what a `{rave}` loading line says. */
  festival?: string;
  logoUrl?: string;
  /**
   * The logo ITSELF, as a data URL, so the next launch draws it with no
   * network and no service worker — an iPhone's web view may have neither
   * (the user, 2026-10-07: "load from cache if it exists, in offline mode").
   * Only for a logo under `LOGO_DATA_MAX`; a bigger one stays a URL.
   */
  logoData?: string;
  /** An SVG logo is drawn as a mask in the accent, as the header draws it. */
  logoSvg?: boolean;
  accent?: string;
  /**
   * "24 July – 4 August 2026" — formatted when kept, in the language of THAT moment. Kept for an older reader only:
   * a screen formats the dates below in the language it shows NOW (the user, 2026-10-29: the dates came up in Hebrew
   * on an English loading screen — kept from a visit in Hebrew).
   */
  when?: string;
  /** The rave's dates and zone, raw, for the screen to say in its own language. */
  gatesOpenAt?: string;
  endsAt?: string;
  timezone?: string;
  /** "Dádpuszta · HU" */
  where?: string;
}

const isSvg = (url: string) => /\.svg(\?|#|$)/i.test(url);

export function splashFrom(bundle: EditionBundle, locale = 'en'): SplashRave {
  const { edition, festival } = bundle;
  const logoUrl = edition.logoUrl ?? festival.logoUrl;
  const where = [edition.venue, festival.country].filter(Boolean).join(' · ');
  return {
    name: edition.name,
    festival: festival.name,
    ...(logoUrl ? { logoUrl, logoSvg: isSvg(logoUrl) } : {}),
    ...(edition.accentColor ? { accent: edition.accentColor } : {}),
    ...(edition.gatesOpenAt && edition.endsAt
      ? {
        when: localDateRange(locale, formatDateRange(new Date(edition.gatesOpenAt), new Date(edition.endsAt), edition.timezone), new Date(edition.gatesOpenAt), new Date(edition.endsAt), edition.timezone),
        gatesOpenAt: edition.gatesOpenAt, endsAt: edition.endsAt, timezone: edition.timezone,
      }
      : {}),
    ...(where ? { where } : {}),
  };
}

/** Bigger than this and the logo stays a URL — localStorage is not an image cache. */
export const LOGO_DATA_MAX = 200_000;

export function rememberSplash(bundle: EditionBundle, locale = 'en'): void {
  // A public page's preview is a picture of the rave, not this device opening it.
  if (inPreview()) return;
  const fresh = splashFrom(bundle, locale);
  const before = readJson<SplashRave | null>(null, SPLASH_NAME, null);
  // The same logo as last time keeps the bytes already held.
  const kept = before?.logoData && before.logoUrl === fresh.logoUrl ? { logoData: before.logoData } : {};
  writeJson(null, SPLASH_NAME, { ...fresh, ...kept });
  if (fresh.logoUrl && !kept.logoData) void holdLogo(fresh.logoUrl);
}

/** Fetch the logo once and keep it beside the rest — never in the way, never an error. */
async function holdLogo(url: string): Promise<void> {
  try {
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) return;
    const blob = await res.blob();
    if (blob.size > LOGO_DATA_MAX || !blob.type.startsWith('image/')) return;
    const data = await new Promise<string>((done, fail) => {
      const reader = new FileReader();
      reader.onload = () => done(String(reader.result));
      reader.onerror = () => fail(reader.error);
      reader.readAsDataURL(blob);
    });
    const now = readJson<SplashRave | null>(null, SPLASH_NAME, null);
    // Only if the rave did not change while it downloaded.
    if (now?.logoUrl === url) writeJson(null, SPLASH_NAME, { ...now, logoData: data });
  } catch {
    // Offline, CORS, a full storage: the URL is still there.
  }
}
